import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { DashboardStats } from '@/api/users';
import { niceAxis, percentDelta } from '@/lib/chart';
import { ACTIVITY_PERIOD_LABELS, labelOf } from '@/lib/labels';
import { useTokenColors } from '@/hooks/useTokenColors';
import { cn } from '@/lib/utils';

/**
 * Бэкенд нумерует дни 1..N, где N — сегодня (users.service.ts, getDailyActiveUsersData).
 * Переводим номер в дату.
 */
function dayDate(day: string | number, totalDays: number): Date | null {
  const n = Number(day);
  if (!Number.isFinite(n)) return null;
  const d = new Date();
  d.setDate(d.getDate() - (totalDays - n));
  return d;
}

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

interface ChartsProps {
  stats: DashboardStats | null;
  loading: boolean;
  periodDays: number;
}

interface TooltipEntry {
  dataKey?: string | number;
  value?: number;
  color?: string;
}

const SERIES_LABEL: Record<string, string> = {
  registrations: 'Регистрации',
  successfulPayments: 'Оплаты',
};

function ChartTooltip({ active, payload, label, totalDays }: { active?: boolean; payload?: TooltipEntry[]; label?: string | number; totalDays: number }) {
  if (!active || !payload?.length) return null;
  const date = label !== undefined ? dayDate(label, totalDays) : null;
  return (
    <div className="rounded-xl border border-border bg-surface px-3.5 py-2.5 text-text shadow-pop">
      <p className="mb-1.5 text-[13px] font-semibold">
        {date ? date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' }) : ''}
      </p>
      {payload.map((e) => (
        <div key={String(e.dataKey)} className="flex items-center gap-2 text-[13px]">
          <span className="size-2.5 rounded-[3px]" style={{ backgroundColor: e.color }} aria-hidden />
          <span className="text-text-2">{SERIES_LABEL[String(e.dataKey)] ?? e.dataKey}:</span>
          <span className="font-semibold tabular-nums">{fmt(e.value ?? 0)}</span>
        </div>
      ))}
    </div>
  );
}

function LegendDot({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden className={cn('size-2.5 rounded-[3px]', className)} />
      {children}
    </span>
  );
}

/** «Регистрации и оплаты» по дням (столбцы) + «Активные пользователи» (день/неделя/месяц к прошлому). */
export default function Charts({ stats, loading, periodDays }: ChartsProps) {
  const c = useTokenColors();
  const totalDays = stats?.dailyActiveUsers.length ?? 30;
  const data = (stats?.dailyActiveUsers ?? []).slice(-periodDays);
  const axis = niceAxis(data.flatMap((d) => [d.registrations, d.successfulPayments]));
  const totalRegs = data.reduce((a, d) => a + d.registrations, 0);
  const totalPays = data.reduce((a, d) => a + d.successfulPayments, 0);

  const periods = stats?.activeUsersByPeriod ?? [];
  const periodMax = Math.max(1, ...periods.map((p) => p.current));

  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
      <Card
        role="region"
        aria-label="Регистрации и оплаты по дням"
        className="flex min-w-0 flex-col gap-3.5 p-[18px] xl:col-span-2"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <b className="font-display text-[17px] font-bold">Регистрации и оплаты</b>
          <div className="flex gap-4 text-[13px] text-text-2">
            <LegendDot className="bg-text">Регистрации{stats ? ` · ${fmt(totalRegs)}` : ''}</LegendDot>
            <LegendDot className="bg-accent">Оплаты{stats ? ` · ${fmt(totalPays)}` : ''}</LegendDot>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 0, left: -18, bottom: 0 }} barGap={2} barCategoryGap="22%">
                <CartesianGrid stroke={c['border-soft']} vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke={c.border}
                  tick={{ fill: c['text-2'], fontSize: 11 }}
                  tickLine={false}
                  tickFormatter={(v) => String(dayDate(v, totalDays)?.getDate() ?? v)}
                  minTickGap={4}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: c['text-2'], fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, axis.max]}
                  ticks={axis.ticks}
                  allowDecimals={false}
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : String(v))}
                />
                <Tooltip
                  cursor={{ fill: c.muted, opacity: 0.6 }}
                  content={<ChartTooltip totalDays={totalDays} />}
                />
                <Bar dataKey="registrations" fill={c.text} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                <Bar dataKey="successfulPayments" fill={c.accent} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card role="region" aria-label="Активные пользователи по периодам" className="flex flex-col gap-3.5 p-[18px]">
        <div className="flex flex-col gap-1">
          <b className="font-display text-[17px] font-bold">Активные пользователи</b>
          <span className="text-[13px] text-text-2">К предыдущему такому же периоду</span>
        </div>
        {loading ? (
          <div className="flex flex-col gap-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {periods.map((p) => {
              const delta = percentDelta(p.current, p.previous);
              return (
                <li key={p.name} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold">{labelOf(ACTIVITY_PERIOD_LABELS, p.name)}</span>
                    <span className="tabular-nums">{fmt(p.current)}</span>
                  </div>
                  <span className="block h-2 rounded-full bg-muted" aria-hidden>
                    <span
                      className="block h-full rounded-full bg-text"
                      style={{ width: `${Math.round((p.current / periodMax) * 100)}%` }}
                    />
                  </span>
                  <span className="text-xs text-text-2">
                    Прошлый: <span className="tabular-nums">{fmt(p.previous)}</span>
                    {delta && (
                      <span
                        className={cn(
                          'ml-1.5 font-semibold',
                          delta.tone === 'pos' ? 'text-pos' : delta.tone === 'neg' ? 'text-neg' : 'text-text-2',
                        )}
                      >
                        {delta.text}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
